import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Select,
  Flex,
} from "@chakra-ui/react";
import { DrawerFormField } from "pages/Borrowers/elements/AddBorrower";
import { AlertsContext } from "providers/alerts";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useContext, useEffect, useRef, useState } from "react";
import { apiRequest } from "utils/api";
import { getErrorMessage } from "utils/errorHandler";
import { getCustomButtonVariant } from "utils/uiconfighelper";
import { useQuery } from "@tanstack/react-query";
import LocationAutocomplete from "components/elements/LocationAutocomplete";
import { useEntitiesForTheCompany } from "hooks/useEntitiesForTheCompany";
import { OptionBase, PropsValue, CreatableSelect } from "chakra-react-select";
import { useRoles } from "hooks/useRoles";
import { selectChakraStyles } from "utils/constants";
import colors from "utils/colors";
import { Json } from "types/supabase";
import { BorrowerCompanySelect } from "components/loans/CreateNewLoanApplication/components/BorrowerCompanySelect";
import { useBorrowerCompaniesForTheTenant } from "hooks/useBorrowerCompaniesForTheTenant";

interface CompanyOption extends OptionBase {
  label: string;
  value: string;
  address?: string | Json | null;
}

function isCompanyOption(option: unknown): option is CompanyOption {
  return !!option && typeof option === 'object' && !Array.isArray(option) && 'value' in option && 'label' in option;
}

const EditBorrower = ({ borrower, loadBorrower }: { borrower: any; loadBorrower: () => void }) => {
  const { showSuccess, showError } = useContext(AlertsContext);
  const { user } = useTenant();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [companyAddress, setCompanyAddress] = useState<any>({});
  const [jobTitle, setJobTitle] = useState("");

  const { data: borrowerCompanies } = useBorrowerCompaniesForTheTenant();
  const [borrowerCompanyId, setBorrowerCompanyId] = useState<string | undefined>(borrower.company.id);
  const [entity, setEntity] = useState<string | undefined>(borrower.entity?.id);
  const { data: entities } = useEntitiesForTheCompany(borrowerCompanyId);
  const { roles: borrowerRoles, isLoading: isLoadingRoles } = useRoles({
    company_id: user.lender_company.id,
    userType: "borrower",
  });
  const borrowerRoleOptions = (borrowerRoles || []).map((role: any) => ({
    label: role.name,
    value: role.id,
  }));
  const [role, setRole] = useState<PropsValue<CompanyOption>>(
    borrower.role?.id && borrower.role?.name
      ? { label: borrower.role.name, value: borrower.role.id }
      : null
  );
  function handleCompanyChange(newValue: string | undefined) {
    setEntity(undefined);
    setBorrowerCompanyId(newValue);
    setRole(null);
  }

  async function saveChanges() {
    setSaving(true);

    try {
      await apiRequest("/supabase/users/update", {
        id: borrower.id,
        values: {
          email,
          first_name: firstName,
          last_name: lastName,
          role: isCompanyOption(role) ? role.value : undefined,
          entity: entity,
          job_title: jobTitle,
        },
        company_address: companyAddress,
        company_name: companyName,
      });
      showSuccess({
        title: "Saved Changes",
        description: "The borrower details have been updated.",
      });
      loadBorrower();
      onClose();
    } catch (err) {
      showError({
        title: "Save Failed",
        description: getErrorMessage(err),
      });
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    setFirstName(borrower.first_name);
    setLastName(borrower.last_name);
    setEmail(borrower.email);
    setCompanyName(borrower.company.name);
    setCompanyAddress((borrower.company || {}).address);
    setEntity(borrower.entity?.id || null);
    setJobTitle(borrower.user?.job_title || borrower.job_title || "");
    setRole(
      borrower.role?.id && borrower.role?.name
        ? { label: borrower.role.name, value: borrower.role.id }
        : null
    );
  }, [borrower]);

  return (
    <Box>
      <Button ref={btnRef} onClick={onOpen} variant="ghost" size="sm" color="primary">
        Edit Details
      </Button>
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} finalFocusRef={btnRef} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Edit Borrower</DrawerHeader>
          <DrawerBody
            overflowY="auto"
            css={{
              "&::-webkit-scrollbar": {
                width: "4px",
              },
              "&::-webkit-scrollbar-track": {
                width: "6px",
                background: "#f1f1f1",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "24px",
              },
            }}
          >
            <Flex direction="column">
              <DrawerFormField
                label="First Name"
                placeholder="John"
                value={firstName}
                onChange={setFirstName}
                cy="edit-borrower-first-name"
              />
              <DrawerFormField
                label="Last Name"
                placeholder="Doe"
                value={lastName}
                onChange={setLastName}
                cy="edit-borrower-last-name"
              />
              <DrawerFormField
                label="Email Address"
                placeholder="Doe"
                value={email}
                onChange={setEmail}
                type="email"
                cy="edit-borrower-email"
              />
              <FormControl mb="16px" isRequired>
                <FormLabel mb="7px" fontWeight="600" fontSize="17px" lineHeight="28px">
                  Company
                </FormLabel>
                <BorrowerCompanySelect
                  borrowerCompanyId={borrowerCompanyId}
                  setBorrowerCompanyId={handleCompanyChange}
                />
              </FormControl>
              {borrowerCompanyId && entities && entities.length > 0 && (
                <FormControl mb="16px">
                  <FormLabel mb="7px" fontWeight="600" fontSize="17px" lineHeight="28px">
                    Entity
                  </FormLabel>
                  <Select
                    placeholder="Select an entity"
                    value={entity || ""}
                    onChange={ev => setEntity(ev.target.value)}
                  >
                    {entities.map(entity => (
                      <option value={entity.id} key={entity.id}>
                        {entity.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}
              <FormControl mb="16px">
                <FormLabel mb="7px" fontWeight="600" fontSize="17px" lineHeight="28px">
                  Company Address
                </FormLabel>
                <LocationAutocomplete
                  onChange={setCompanyAddress}
                  defaultValue={(companyAddress || {}).formatted || ""}
                />
              </FormControl>
              <DrawerFormField
                label="Job Title"
                placeholder="Chief Lendtech Officer"
                value={jobTitle}
                onChange={setJobTitle}
                cy="edit-borrower-job-title"
                isRequired={false}
              />
              <FormControl mb="16px" isRequired>
                <FormLabel mb="7px" fontWeight="600" fontSize="17px" lineHeight="28px">
                  Role
                </FormLabel>
                <CreatableSelect
                  className="borrower-role-select"
                  useBasicStyles
                  selectedOptionColor="black"
                  colorScheme="purple"
                  variant="outline"
                  chakraStyles={selectChakraStyles}
                  value={role}
                  hideSelectedOptions
                  focusBorderColor={colors.GRAY_50}
                  onChange={(val: any) => setRole(val)}
                  options={borrowerRoleOptions}
                  isLoading={isLoadingRoles}
                  noOptionsMessage={() => "No roles found for company."}
                />
              </FormControl>
            </Flex>
          </DrawerBody>
          <DrawerFooter>
            <Flex gap={2} flex={1}>
              <Button variant="outline" onClick={onClose} flex={1}>
                Cancel
              </Button>
              <Button
                onClick={saveChanges}
                data-cy="invite-borrower-send-btn"
                variant={getCustomButtonVariant(user)}
                flex="1"
                isLoading={saving}
                loadingText="Sending"
              >
                Save Changes
              </Button>
            </Flex>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default EditBorrower;
